import { computed, ref } from 'vue'
import { colors } from 'quasar'
import { defineStore } from 'pinia'
import { accountingApi } from '@/apis/accounting.api'
import { getI18n, getLocalizedEnumItems, getLocalizedItems, getTranslatedItems } from '@/plugins/i18n'
import { useFetchAndStoreAsync } from '@/composables/store'
import { withEntityChange } from '@/composables/entity-changes'
import { entityChangeTypeEnum } from '@/stores/portal.store'
import { buildTagPayload } from '@/composables/tag'

export const DEFAULT_EXPENSE_ITEM_ICON = 'mdi-receipt-text-outline'
export const DEFAULT_EXPENSE_ITEM_COLOR = '#737C8C'

export const initAccountingStoreAsync = async() => {}

export const useAccountingStore = defineStore('accounting', () => {
  const api = accountingApi
  const companyDataSectionEnum = Object.freeze({
    LOCATIONS: 0,
  })
  const companyDataTabEnum = Object.freeze({
    GENERAL: 0,
    PERMISSIONS: 1,
    ACCOUNTING: 2,
    EXCHANGE_RATES: 3,
    PROFILE: 4,
  })

  const costValorizationsEnum = Object.freeze({
    PRICE: 0,
    COST: 1
  })
  const costReportRowTypeEnum = Object.freeze({
    TIME: 1,
    EXPENSE: 2,
    COMMENT: 3,
  })
  const expenseClaimStatusEnum = Object.freeze({
    DRAFT: 0,
    SUBMITTED: 1,
    APPROVED: 2,
    PAID: 3,
    REJECTED: 4,
  })
  const expenseClaimScopeEnum = Object.freeze({
    STAFF: 0,
    MINE: 1,
  })
  const expenseClaimStatusDefinitions = Object.freeze([
    { id: expenseClaimStatusEnum.DRAFT, key: 'draft', color: 'grey-6' },
    { id: expenseClaimStatusEnum.SUBMITTED, key: 'submitted', color: 'primary' },
    { id: expenseClaimStatusEnum.APPROVED, key: 'approved', color: 'positive' },
    { id: expenseClaimStatusEnum.PAID, key: 'paid', color: 'teal' },
    { id: expenseClaimStatusEnum.REJECTED, key: 'rejected', color: 'warning' },
  ])
  const costReportRowLockEnum = Object.freeze({
    INVOICE: 0,
    EXPENSE_CLAIM: 1,
  })
  const invoiceQuoteUpdateEnum = Object.freeze({
    UPDATE_QUOTE: 0,
    KEEP_QUOTE: 1,
  })
  const invoiceStatusEnum = Object.freeze({
    DRAFT: 0,
    SENT: 1,
    PAID: 2,
    CANCELLED: 3,
  })
  const invoiceStatusColorEnum = Object.freeze({
    DRAFT: 'grey-6',
    SENT: 'blue',
    PAID: 'positive',
    CANCELLED: 'negative',
  })
  const invoiceLineFieldTypeEnum = Object.freeze({
    QUANTITY: 0,
    NUMBER: 1,
    PERCENT: 2,
    TEXT: 3,
  })
  const budgetDocumentGroupRowsByEnum = Object.freeze({
    NONE: 0,
    DAY: 1,
    PRIMARY_TAG: 2
  })
  const budgetDocumentGroupRowsByLabels = Object.freeze({
    [budgetDocumentGroupRowsByEnum.NONE]: 'common.groupBy.none',
    [budgetDocumentGroupRowsByEnum.DAY]: 'common.groupBy.day',
    [budgetDocumentGroupRowsByEnum.PRIMARY_TAG]: 'common.groupBy.tags',
  })
  const quoteDeleteModeEnum = Object.freeze({
    QUOTE_AND_PROJECTS: 'quote_and_projects',
    QUOTE_ONLY: 'quote_only',
  })
  const invoiceNumberFormatTokens = Object.freeze({
    YEAR: '{year}',
    NUMBER: '{number}',
  })
  const documentPrintLayoutEnum = Object.freeze({
    CLASSIC: 0,
    MINIMAL: 1,
    BOLD_BAND: 2,
    CINEMATIC: 3,
    EDITORIAL: 4,
    SIDEBAR: 5,
    SPLIT_HEADER: 6,
    RENTAL_COMPACT: 7,
    LEDGER: 8,
    BOUTIQUE: 9,
  })
  const printDocumentTypeEnum = Object.freeze({
    QUOTE: 0,
    INVOICE: 1,
    REPORT: 2,
  })
  const reportTypeEnum = Object.freeze({
    STAFF_ACTIVITY: 0,
    COMPANY_ACTIVITY: 2,
    PROJECT_COST_REPORT: 4,
  })
  const printSettingTypeEnum = Object.freeze({
    ...reportTypeEnum,
    QUOTE: 5,
    INVOICE: 6,
    DELIVERY_NOTE: 7,
  })
  const reportColumnTemplateDefaultFields = Object.freeze({
    [printSettingTypeEnum.STAFF_ACTIVITY]: 'default_staff_activity_column_template',
    [printSettingTypeEnum.COMPANY_ACTIVITY]: 'default_company_activity_column_template',
    [printSettingTypeEnum.PROJECT_COST_REPORT]: 'default_project_cost_report_column_template',
    [printSettingTypeEnum.QUOTE]: 'default_quote_column_template',
    [printSettingTypeEnum.INVOICE]: 'default_invoice_column_template',
    [printSettingTypeEnum.DELIVERY_NOTE]: 'default_delivery_note_column_template',
  })
  const reportSubjectEnum = Object.freeze({
    STAFF: 0,
    COMPANY: 1,
  })
  const reportTimeBasisEnum = Object.freeze({
    WORK: 0,
    REPORTED: 1,
  })
  const reportRowKindEnum = Object.freeze({
    TIME: 0,
    EXPENSE: 1,
    SERVICE: 2,
  })
  const reportScopeEnum = Object.freeze({
    COMPANY: 0,
    LISTING: 1,
  })
  const reportTimeNoteEnum = Object.freeze({
    NONE: 0,
    ASSUMED_START: 1,
  })
  const reportFormatEnum = Object.freeze({
    JSON: 0,
    PDF: 1,
    XLSX: 2,
  })
  const reportColumnGroupEnum = Object.freeze({
    BASE: 0,
    TIMES: 1,
    NOTES: 2,
    AMOUNTS: 3,
    INTERNAL: 4,
  })
  const reportColumnKindEnum = Object.freeze({
    TEXT: 0,
    DATE: 1,
    TIME: 2,
    DURATION: 3,
    SIGNED_DURATION: 4,
    MONEY: 5,
    REFERENCE_MONEY: 6,
    NUMBER: 7,
    RATE: 8,
    PERCENT: 9,
    MONEY_BY_CURRENCY: 10,
    EXCHANGE_RATE: 11,
    TIME_NOTE: 12,
  })
  const reportColumnPresetEnum = Object.freeze({
    CLIENT: 0,
    TO_SIGN: 1,
    INTERNAL: 2,
    CUSTOM: 3,
  })
  const reportGroupByEnum = Object.freeze({
    WEEK: 0,
    PROJECT: 1,
    NONE: 2,
  })
  const reportOrientationEnum = Object.freeze({
    AUTO: 0,
    PORTRAIT: 1,
    LANDSCAPE: 2,
  })
  const reportDurationFormatEnum = Object.freeze({
    HOURS_MINUTES: 0,
    DECIMAL: 1,
  })
  const reportExcelSheetEnum = Object.freeze({
    SOURCE_ROWS: 0,
    ADJUSTMENTS: 1,
    PARAMETERS: 2,
  })
  const reportFileStepEnum = Object.freeze({ DOCUMENT: 0, PRINT: 1, EXCEL: 2 })
  const budgetTagKindEnum = Object.freeze({ GENERIC: 0, LOCATION: 1 })
  const reportSectionKeyEnum = Object.freeze({
    DAILY: 'daily',
    STAFF_BREAKDOWN: 'staff_breakdown',
    PROJECTS: 'projects',
    HOURS: 'hours',
    EXPENSES: 'expenses',
    QUOTES: 'quotes',
    INVOICES: 'invoices',
    PROJECTS_CLIENTS: 'projects_clients',
  })
  const expenseSectionKeys = Object.freeze([reportSectionKeyEnum.EXPENSES])
  const reportSectionIcons = Object.freeze({
    [reportSectionKeyEnum.DAILY]: 'mdi-calendar-today-outline',
    [reportSectionKeyEnum.STAFF_BREAKDOWN]: 'mdi-account-group-outline',
    [reportSectionKeyEnum.PROJECTS]: 'mdi-briefcase-outline',
    [reportSectionKeyEnum.HOURS]: 'mdi-clock-outline',
    [reportSectionKeyEnum.EXPENSES]: 'mdi-tag-outline',
    [reportSectionKeyEnum.QUOTES]: 'mdi-file-document-check-outline',
    [reportSectionKeyEnum.INVOICES]: 'mdi-file-document-outline',
    [reportSectionKeyEnum.PROJECTS_CLIENTS]: 'mdi-briefcase-account-outline',
  })
  const reportTypeDefinitions = Object.freeze([
    { id: reportTypeEnum.STAFF_ACTIVITY, key: 'staffActivity', icon: 'mdi-account-clock-outline', color: 'primary' },
    { id: reportTypeEnum.COMPANY_ACTIVITY, key: 'companyActivity', icon: 'mdi-domain', color: 'pink-6' },
    { id: reportTypeEnum.PROJECT_COST_REPORT, key: 'projectCostReport', icon: 'mdi-file-chart-outline', color: 'orange-8' },
  ])
  const documentPrintSettingTypeDefinitions = Object.freeze([
    { id: printSettingTypeEnum.QUOTE, label: 'common.budgetDocument', icon: 'mdi-file-document-edit-outline', color: 'teal-7' },
    { id: printSettingTypeEnum.INVOICE, label: 'common.invoice', icon: 'mdi-receipt-text-outline', color: 'indigo-6' },
    { id: printSettingTypeEnum.DELIVERY_NOTE, label: 'common.deliveryNote', icon: 'mdi-truck-delivery-outline', color: 'brown-6' },
  ])
  const reportSubjectDefinitions = Object.freeze([
    { id: reportSubjectEnum.STAFF, key: 'staff', label: 'common.staff', icon: 'mdi-account', color: 'primary' },
    { id: reportSubjectEnum.COMPANY, key: 'company', label: 'common.company', icon: 'mdi-domain', color: 'pink-6' },
  ])
  const printFontEnum = Object.freeze({
    SANS: 0,
    SERIF: 1,
    MONO: 2,
  })
  const printLogoPositionEnum = Object.freeze({
    LEFT: 0,
    CENTER: 1,
    RIGHT: 2,
  })
  const printLogoSizeEnum = Object.freeze({
    SMALL: 0,
    MEDIUM: 1,
    LARGE: 2,
  })
  const printTotalsStyleEnum = Object.freeze({
    LINE: 0,
    BOX: 1,
    BAND: 2,
  })
  const printTemplateDeleteModeEnum = Object.freeze({
    REASSIGN_TO_DEFAULTS: 'reassign_to_defaults',
  })
  const documentTermTemplateTypeEnum = Object.freeze({
    PAYMENT_TERMS: 0,
    BUDGET_ESTIMATE_TERMS: 1,
    INTELLECTUAL_PROPERTY_TERMS: 2,
    PAYMENT_COORDINATES: 3,
    EXPENSE_DECLARATION: 4,
  })
  const reportColumns = ref({})
  const budgetDocumentRowCategories = ref([])
  const budgetDocumentExportTemplates = ref([])
  const documentPrintTemplates = ref([])
  const reportColumnTemplates = ref([])
  const budgetTags = ref([])
  const companyData = ref({})
  const costReportRowTags = ref([])
  const paymentTermTemplates = ref([])
  const budgetEstimateTermTemplates = ref([])
  const intellectualPropertyTermTemplates = ref([])
  const paymentCoordinatesTemplates = ref([])
  const invoiceSelection = ref({})

  const setInvoiceSelection = (projectId, rows) => {
    invoiceSelection.value = { ...invoiceSelection.value, [projectId]: rows }
  }
  const clearInvoiceSelection = () => { invoiceSelection.value = {} }
  const invoiceSelectionRows = () => Object.values(invoiceSelection.value).flat()
  const upsertInvoiceAsync = (data) => api.upsertInvoiceAsync(data)
  const deleteInvoiceAsync = (id) => api.deleteInvoiceAsync(id)
  const previewInvoiceAsync = (id) => api.previewInvoiceAsync(id)
  const fetchInvoiceByIdAsync = async(id) => api.getAsync(`${api.invoicesUrl}${id}/`)
  const upsertInvoiceRowAsync = (data) => api.upsertAsync(api.invoiceRowsUrl, data)
  const deleteInvoiceRowAsync = (id) => api.deleteAsync(`${api.invoiceRowsUrl}${id}/`)
  const fetchInvoiceRowsAsync = (invoiceId) => api.getAsync(api.invoiceRowsUrl, { invoice: invoiceId })
  const fetchNextInvoiceNumberAsync = async() => (await api.getAsync(`${api.invoicesUrl}next-number/`)).data.next_number
  let invoiceStatusColors = null
  const getInvoiceStatusColors = () => {
    invoiceStatusColors ??= Object.fromEntries(Object.entries(invoiceStatusColorEnum).map(([key, palette]) => [key, colors.getPaletteColor(palette)]))
    return invoiceStatusColors
  }
  const getLocalizedInvoiceStatuses = (count = 1) => Object.keys(invoiceStatusEnum).map(i => ({
    id: invoiceStatusEnum[i],
    name: getI18n().t(`common.invoiceStatuses.${i.toLowerCase()}`, count),
    palette: invoiceStatusColorEnum[i],
    color: getInvoiceStatusColors()[i],
  }))
  const getLocalizedInvoiceStatus = (status) => getLocalizedInvoiceStatuses().find(item => item.id === status)

  const upsertCostReportRowTagAsync = (data) => api.upsertAsync(api.costReportRowTagsUrl, data)
  const deleteCostReportRowTagAsync = (id) => api.deleteAsync(`${api.costReportRowTagsUrl}${id}/`)
  const deleteCostReportRowAsync = (id, config) => api.deleteCostReportRowAsync(id, config)
  const downloadCostReportRowFileAsync = (id, fileName) => api.downloadCostReportRowFileAsync(id, fileName)
  const getCostReportRowFileUrlAsync = (id) => api.getBlobUrlAsync(`${api.costReportRowsUrl}files/${id}`)
  const fetchCostReportRowTagsAsync = async(params, force = false) => useFetchAndStoreAsync(costReportRowTags, () => api.getCostReportRowTagsAsync(params), force)
  const getLocalizedCostReportRowTags = () => getTranslatedItems(costReportRowTags.value)
  const costReportRowTagById = computed(() => new Map(getLocalizedCostReportRowTags().map(tag => [tag.id, tag])))
  const upsertCostReportRowAsync = withEntityChange([entityChangeTypeEnum.COST_REPORT_ROW], (data) => api.upsertCostReportRowAsync(data))

  const getLocalizedDefinitions = (definitions, labelPrefix) => {
    const i18n = getI18n()
    return definitions.map(definition => ({ ...definition, name: i18n.t(`${labelPrefix}.${definition.key}`) }))
  }
  const getLocalizedExpenseClaimStatuses = () => getLocalizedDefinitions(expenseClaimStatusDefinitions, 'common.expenseClaimStatuses')

  const fetchExpenseClaimByIdAsync = async(id) => (await api.getAsync(`${api.expenseClaimsUrl}${id}/`)).data
  const fetchExpenseClaimRowsAsync = async(id) => (await api.getAsync(`${api.expenseClaimsUrl}${id}/rows/`)).data
  const saveExpenseClaimAsync = async(data, id = null) => (id
    ? await api.patchAsync(`${api.expenseClaimsUrl}${id}/`, data)
    : await api.postAsync(api.expenseClaimsUrl, data)).data
  const fetchClaimableExpenseRowsAsync = async(staff, claimId = null) => (await api.getCostReportRowsAsync({
    staff, type: costReportRowTypeEnum.EXPENSE, claimable: true, ...(claimId && { expense_claim: claimId }),
  })).data
  const transitionExpenseClaimsAsync = async(data) => (await api.postAsync(`${api.expenseClaimsUrl}transition/`, data)).data
  const deleteExpenseClaimAsync = (id) => api.deleteAsync(`${api.expenseClaimsUrl}${id}/`)

  const addBudgetTagAsync = async(tag) => api.addBudgetTagAsync(tag)

  const generateProjectFromBudgetDocumentAsync = async(documentId, data) => api.generateProjectFromBudgetDocumentAsync(documentId, data)

  const fetchBudgetDocumentRowCategoriesAsync = async(params, store = false) => useFetchAndStoreAsync(budgetDocumentRowCategories, () => api.getBudgetDocumentRowCategoriesAsync(params), store)
  const fetchBudgetDocumentExportTemplatesAsync = async(params, store = false) => useFetchAndStoreAsync(budgetDocumentExportTemplates, () => api.getBudgetDocumentExportTemplatesAsync(params), store)
  const fetchBudgetTagsAsync = async(params, force = false) => useFetchAndStoreAsync(budgetTags, () => api.getBudgetTagsAsync(params), force)
  const fetchBudgetDocumentsInRangeAsync = async(params) => api.getBudgetDocumentsAsync(params)
  const fetchCompanyDataAsync = async(params, force = false) => useFetchAndStoreAsync(companyData, () => api.getCompanyDataAsync(params), force)
  const fetchPaymentTermTemplatesAsync = async(params, force = false) => useFetchAndStoreAsync(paymentTermTemplates, () => api.getDocumentTermTemplatesAsync({ ...params, term_type: documentTermTemplateTypeEnum.PAYMENT_TERMS }), force)
  const fetchBudgetEstimateTermTemplatesAsync = async(params, force = false) => useFetchAndStoreAsync(budgetEstimateTermTemplates, () => api.getDocumentTermTemplatesAsync({ ...params, term_type: documentTermTemplateTypeEnum.BUDGET_ESTIMATE_TERMS }), force)
  const fetchIntellectualPropertyTermTemplatesAsync = async(params, force = false) => useFetchAndStoreAsync(intellectualPropertyTermTemplates, () => api.getDocumentTermTemplatesAsync({ ...params, term_type: documentTermTemplateTypeEnum.INTELLECTUAL_PROPERTY_TERMS }), force)
  const fetchPaymentCoordinatesTemplatesAsync = async(params, force = false) => useFetchAndStoreAsync(paymentCoordinatesTemplates, () => api.getDocumentTermTemplatesAsync({ ...params, term_type: documentTermTemplateTypeEnum.PAYMENT_COORDINATES }), force)
  const deleteDocumentTermTemplateAsync = (id) => api.deleteDocumentTermTemplateAsync(id)
  let documentPrintTemplatesRequest = null
  const fetchDocumentPrintTemplatesAsync = async(params, store = false) => useFetchAndStoreAsync(documentPrintTemplates, () => api.getAsync(api.documentPrintTemplatesUrl, params), store)
  const ensureDocumentPrintTemplatesAsync = () => {
    documentPrintTemplatesRequest ??= documentPrintTemplates.value.length ? Promise.resolve() : fetchDocumentPrintTemplatesAsync({}, true)
    return documentPrintTemplatesRequest
  }
  const getLocalizedDocumentPrintTemplates = () => getTranslatedItems(documentPrintTemplates.value)
  const upsertDocumentPrintTemplateAsync = (data) => api.upsertAsync(api.documentPrintTemplatesUrl, data)
  const deleteDocumentPrintTemplateAsync = (id, config) => api.deleteAsync(`${api.documentPrintTemplatesUrl}${id}/`, config)
  const previewDocumentPrintTemplateAsync = (params) => api.getBlobUrlAsync(`${api.documentPrintTemplatesUrl}preview/`, params)
  const getLocalizedPrintLayouts = () => {
    const i18n = getI18n()
    return Object.keys(documentPrintLayoutEnum).map(key => ({
      id: documentPrintLayoutEnum[key],
      name: i18n.t(`common.printLayouts.${key.toLowerCase()}`),
      description: i18n.t(`common.printLayoutDescriptions.${key.toLowerCase()}`),
    }))
  }
  const getLocalizedPrintFonts = () => getLocalizedEnumItems(printFontEnum, 'common.printFonts')
  const getLocalizedPrintLogoPositions = () => getLocalizedEnumItems(printLogoPositionEnum, 'common.printLogoPositions')
  const getLocalizedPrintLogoSizes = () => getLocalizedEnumItems(printLogoSizeEnum, 'common.printLogoSizes')
  const getLocalizedPrintTotalsStyles = () => getLocalizedEnumItems(printTotalsStyleEnum, 'common.printTotalsStyles')
  const upsertDocumentTermTemplateByTitleAsync = (data) => api.upsertDocumentTermTemplateByTitleAsync(data)
  const upsertDocumentTermTemplateAsync = (data) => api.upsertAsync(api.documentTermTemplatesUrl, data)

  const archiveBudgetDocumentAsync = (documentId) => api.archiveBudgetDocumentAsync(documentId)

  const cloneBudgetDocumentAsync = async(documentId, data) => api.cloneBudgetDocumentAsync(documentId, data)

  const deleteBudgetDocumentAsync = (documentId, config) => api.deleteBudgetDocumentAsync(documentId, config)
  const exportBudgetDocumentToExcelAsync = async(documentId, fileName, data) => api.exportBudgetDocumentToExcelAsync(documentId, fileName, data)

  const getDefaultCostValorization = () => costValorizationsEnum.PRICE
  const fetchBudgetDocumentRowByIdAsync = async(id) => (await api.getAsync(`${api.budgetDocumentRowsUrl}${id}/`)).data
  const getBudgetDocumentRowCategoryById = (id) => budgetDocumentRowCategories.value.find(c => c.id === id)

  const getCompanyInitials = () => {
    const companyName = companyData.value?.name
    if (!companyName) return ''
    const companyNameParts = companyName.split(' ')
    return companyNameParts.map(part => part.charAt(0).toUpperCase()).join('')
  }

  const getCompanyLogoUrl = () => {
    if (!companyData.value) return ''
    // Uploaded media is same-origin; the API's absolute URL can carry the wrong port/scheme behind the proxy
    return companyData.value.logo ? new URL(companyData.value.logo).pathname : (companyData.value.logo_url || '')
  }

  const getLocalizedBudgetTags = () => getTranslatedItems(budgetTags.value).sort((a, b) => a.name?.localeCompare(b.name))
  const getLocalizedBudgetDocumentRowCategories = () => getTranslatedItems(budgetDocumentRowCategories.value)
  const getLocalizedBudgetDocumentExportTemplates = () => getTranslatedItems(budgetDocumentExportTemplates.value)
  const getLocalizedCostValorizations = () => getLocalizedItems(Object.keys(costValorizationsEnum).map(i => ({id: costValorizationsEnum[i], name: `common.costValorizations.${i.toLowerCase()}`})))
  const getBudgetDocumentMagicLinkAsync = async(id, params) => api.getBudgetDocumentMagicLinkAsync(id, params)

  const printBudgetDocumentAsync = async(id, fileName, params) => api.printBudgetDocumentAsync(id, fileName, params)
  const previewBudgetDocumentAsync = async(id, params) => api.previewBudgetDocumentAsync(id, params)
  const printBudgetDocumentMagicLinkAsync = async(id, params) => api.printBudgetDocumentMagicLinkAsync(id, params)

  const reportPaletteColors = {}
  const getLocalizedReportTypes = () => {
    const i18n = getI18n()
    return reportTypeDefinitions.map(definition => ({
      ...definition,
      name: i18n.t(`print.report.types.${definition.key}`),
      description: i18n.t(`common.report.typeDescriptions.${definition.key}`),
      paletteColor: (reportPaletteColors[definition.color] ??= colors.getPaletteColor(definition.color)),
    }))
  }
  const getLocalizedPrintSettingTypes = () => {
    const i18n = getI18n()
    return [
      ...getLocalizedReportTypes(),
      ...documentPrintSettingTypeDefinitions.map(definition => ({
        ...definition,
        name: i18n.t(definition.label),
        paletteColor: (reportPaletteColors[definition.color] ??= colors.getPaletteColor(definition.color)),
      })),
    ]
  }
  const isReportSettingType = (settingType) => Object.values(reportTypeEnum).includes(settingType)
  const getLocalizedReportSubjects = () => {
    const i18n = getI18n()
    return reportSubjectDefinitions.map(definition => ({
      ...definition,
      name: i18n.t(definition.label),
      description: i18n.t(`common.report.subjectDescriptions.${definition.key}`),
      paletteColor: (reportPaletteColors[definition.color] ??= colors.getPaletteColor(definition.color)),
    }))
  }
  const getLocalizedReportSubject = (subject) => getLocalizedReportSubjects().find(item => item.id === subject)
  const isStaffSubject = (subject) => subject === reportSubjectEnum.STAFF
  const getLocalizedReportColumnPresets = () => getLocalizedEnumItems(reportColumnPresetEnum, 'common.report.presets')
  const fetchReportColumnsAsync = async() => useFetchAndStoreAsync(reportColumns, () => api.getAsync(`${api.reportsUrl}columns/`), true)
  const upsertReportAsync = (data) => api.upsertAsync(api.reportsUrl, data)
  const deleteReportAsync = (id) => api.deleteAsync(`${api.reportsUrl}${id}/`)
  const fetchReportRowsAsync = async(id) => (await api.getAsync(`${api.reportsUrl}${id}/rows/`)).data
  const computeReportAsync = async(data) => (await api.postAsync(`${api.reportsUrl}compute/`, data)).data
  const fetchReportProjectsAsync = async(data) => (await api.postAsync(`${api.reportsUrl}projects/`, data)).data
  const renderReportAsync = async(data) => (await api.postAsync(`${api.reportsUrl}render/`, { ...data, format: reportFormatEnum.JSON })).data
  const previewReportAsync = (data) => api.getBlobFileAsync(`${api.reportsUrl}render/`, {}, { ...data, format: reportFormatEnum.PDF })
  const fetchReportColumnTemplatesAsync = async() => useFetchAndStoreAsync(reportColumnTemplates, () => api.getAsync(api.reportColumnTemplatesUrl), true)
  const upsertReportColumnTemplateAsync = (data) => api.upsertAsync(api.reportColumnTemplatesUrl, data)
  const deleteReportColumnTemplateAsync = (id) => api.deleteAsync(`${api.reportColumnTemplatesUrl}${id}/`)
  const downloadReportExcelAsync = (data) => api.downloadAsync(`${api.reportsUrl}render/`, null, { ...data, format: reportFormatEnum.XLSX }, { method: 'post' })
  const recomputeReportAsync = async(id, dryRun = false) => (await api.recomputeReportAsync(id, dryRun)).data
  const duplicateReportAsync = async(id, data) => (await api.postAsync(`${api.reportsUrl}${id}/duplicate/`, data)).data
  const getLocalizedBudgetTagKinds = () => getLocalizedEnumItems(budgetTagKindEnum, 'common.budgetTagKinds')
  const createBudgetTagAsync = async(tag) => {
    const created = (await api.addBudgetTagAsync(buildTagPayload(tag.name, { is_generic: false, kind: tag.kind, subdivision_code: tag.subdivision_code }))).data
    await fetchBudgetTagsAsync({ all: true }, true)
    return created.id
  }

  const updateCompanyDataAsync = (data) => api.updateCompanyDataAsync(data)
  const updateTagBudgetDocumentRowsAsync = (tagId, data) => api.updateTagBudgetDocumentRowsAsync(tagId, data)
  const getBudgetDocumentSchedulesAsync = (id) => api.getBudgetDocumentSchedulesAsync(id)
  const upsertBudgetDocumentAsync = withEntityChange([entityChangeTypeEnum.QUOTE], (data) => api.upsertBudgetDocumentAsync(data))
  const upsertBudgetDocumentRowAsync = (data) => api.upsertBudgetDocumentRowAsync(data)

  const $reset = () => {
    budgetDocumentRowCategories.value = []
  }

  return {
    companyDataSectionEnum,
    companyDataTabEnum,
    $reset,
    addBudgetTagAsync,
    api,
    costReportRowTags,
    upsertCostReportRowTagAsync,
    deleteCostReportRowTagAsync,
    expenseClaimStatusEnum,
    expenseClaimScopeEnum,
    costReportRowLockEnum,
    getLocalizedExpenseClaimStatuses,
    costReportRowTagById,
    fetchExpenseClaimByIdAsync,
    fetchExpenseClaimRowsAsync,
    saveExpenseClaimAsync,
    fetchClaimableExpenseRowsAsync,
    transitionExpenseClaimsAsync,
    deleteExpenseClaimAsync,
    deleteCostReportRowAsync,
    downloadCostReportRowFileAsync,
    getCostReportRowFileUrlAsync,
    fetchCostReportRowTagsAsync,
    getLocalizedCostReportRowTags,
    upsertCostReportRowAsync,
    archiveBudgetDocumentAsync,
    budgetDocumentExportTemplates,
    budgetDocumentGroupRowsByEnum,
    budgetDocumentGroupRowsByLabels,
    quoteDeleteModeEnum,
    budgetDocumentRowCategories,
    budgetTags,
    cloneBudgetDocumentAsync,
    companyData,
    costReportRowTypeEnum,
    costValorizationsEnum,
    invoiceStatusEnum,
    invoiceStatusColorEnum,
    invoiceLineFieldTypeEnum,
    invoiceQuoteUpdateEnum,
    invoiceNumberFormatTokens,
    invoiceSelection,
    setInvoiceSelection,
    clearInvoiceSelection,
    invoiceSelectionRows,
    upsertInvoiceAsync,
    deleteInvoiceAsync,
    previewInvoiceAsync,
    upsertInvoiceRowAsync,
    deleteInvoiceRowAsync,
    fetchInvoiceRowsAsync,
    fetchNextInvoiceNumberAsync,
    fetchInvoiceByIdAsync,
    getLocalizedInvoiceStatuses,
    getLocalizedInvoiceStatus,
    generateProjectFromBudgetDocumentAsync,
    deleteBudgetDocumentAsync,
    exportBudgetDocumentToExcelAsync,
    fetchBudgetDocumentExportTemplatesAsync,
    fetchBudgetDocumentRowCategoriesAsync,
    fetchBudgetDocumentRowByIdAsync,
    fetchBudgetDocumentsInRangeAsync,
    fetchBudgetTagsAsync,
    fetchCompanyDataAsync,
    budgetEstimateTermTemplates,
    deleteDocumentTermTemplateAsync,
    documentTermTemplateTypeEnum,
    documentPrintLayoutEnum,
    documentPrintTemplates,
    reportColumnTemplates,
    reportColumnTemplateDefaultFields,
    fetchReportColumnTemplatesAsync,
    upsertReportColumnTemplateAsync,
    deleteReportColumnTemplateAsync,
    printDocumentTypeEnum,
    reportTypeEnum,
    printSettingTypeEnum,
    reportSubjectEnum,
    reportTimeBasisEnum,
    reportRowKindEnum,
    reportScopeEnum,
    budgetTagKindEnum,
    getLocalizedBudgetTagKinds,
    createBudgetTagAsync,
    reportTimeNoteEnum,
    reportColumnGroupEnum,
    reportColumnKindEnum,
    reportColumnPresetEnum,
    reportGroupByEnum,
    reportOrientationEnum,
    reportDurationFormatEnum,
    reportExcelSheetEnum,
    reportSectionKeyEnum,
    expenseSectionKeys,
    reportSectionIcons,
    reportFileStepEnum,
    reportColumns,
    getLocalizedReportTypes,
    getLocalizedPrintSettingTypes,
    isReportSettingType,
    getLocalizedReportSubjects,
    getLocalizedReportSubject,
    isStaffSubject,
    getLocalizedReportColumnPresets,
    fetchReportColumnsAsync,
    upsertReportAsync,
    deleteReportAsync,
    fetchReportRowsAsync,
    computeReportAsync,
    fetchReportProjectsAsync,
    renderReportAsync,
    previewReportAsync,
    downloadReportExcelAsync,
    recomputeReportAsync,
    duplicateReportAsync,
    printFontEnum,
    printLogoPositionEnum,
    printLogoSizeEnum,
    printTotalsStyleEnum,
    printTemplateDeleteModeEnum,
    fetchDocumentPrintTemplatesAsync,
    ensureDocumentPrintTemplatesAsync,
    getLocalizedDocumentPrintTemplates,
    getLocalizedPrintLayouts,
    getLocalizedPrintFonts,
    getLocalizedPrintLogoPositions,
    getLocalizedPrintLogoSizes,
    getLocalizedPrintTotalsStyles,
    upsertDocumentPrintTemplateAsync,
    deleteDocumentPrintTemplateAsync,
    previewDocumentPrintTemplateAsync,
    fetchBudgetEstimateTermTemplatesAsync,
    fetchIntellectualPropertyTermTemplatesAsync,
    fetchPaymentCoordinatesTemplatesAsync,
    fetchPaymentTermTemplatesAsync,
    intellectualPropertyTermTemplates,
    paymentCoordinatesTemplates,
    getBudgetDocumentMagicLinkAsync,
    getBudgetDocumentSchedulesAsync,
    getBudgetDocumentRowCategoryById,
    getCompanyInitials,
    getCompanyLogoUrl,
    getDefaultCostValorization,
    getLocalizedBudgetDocumentExportTemplates,
    getLocalizedBudgetDocumentRowCategories,
    getLocalizedBudgetTags,
    getLocalizedCostValorizations,
    paymentTermTemplates,
    printBudgetDocumentAsync,
    printBudgetDocumentMagicLinkAsync,
    previewBudgetDocumentAsync,
    updateCompanyDataAsync,
    updateTagBudgetDocumentRowsAsync,
    upsertBudgetDocumentAsync,
    upsertBudgetDocumentRowAsync,
    upsertDocumentTermTemplateByTitleAsync,
    upsertDocumentTermTemplateAsync,
  }
}, {
  persist: {
    enabled: true,
    strategies: [
      {
        storage: localStorage, paths: [
          'companyData',
          'budgetTags',
          'budgetDocumentRowCategories',
          'budgetDocumentExportTemplates',
          'documentPrintTemplates',
          'costReportRowTags',
          'paymentTermTemplates',
          'budgetEstimateTermTemplates',
          'intellectualPropertyTermTemplates',
          'paymentCoordinatesTemplates',
        ]
      },
    ],
  },
})
